import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/authContext';
import { useResumeManager } from '../../services/useResumeManager';
import { useRecruiterReviewService } from '../../services/useRecruiterReviewService';
import { useAnalyzeService } from '../../services/useAnalyzeService';
import { Resume } from '../../types/resume';
import { ResumeVersion } from '../../types/resumeVersion';
import { JobPostingDto } from '../../types/jobPostingDto';
import { RecruiterReviewResponse } from '../../types/recruiterReview';
import './recruiterreviewpage.css';

const RecruiterReviewPage: FC = () => {
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const { resumes, busy: resumesBusy } = useResumeManager(user?.id);
    const { getPastJobPostingsAnalysis, getPastJobAnalysisByJobPostingId } = useAnalyzeService();
    const { runRecruiterReview } = useRecruiterReviewService();

    const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
    const [selectedVersion, setSelectedVersion] = useState<ResumeVersion | null>(null);
    const [availableVersions, setAvailableVersions] = useState<ResumeVersion[]>([]);

    const [jobPostings, setJobPostings] = useState<JobPostingDto[]>([]);
    const [selectedJobId, setSelectedJobId] = useState<string>(searchParams.get('jobPostingId') ?? '');
    const [jdText, setJdText] = useState<string>('');

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<RecruiterReviewResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!user?.id) return;
        getPastJobPostingsAnalysis(user.id).then(setJobPostings).catch(() => {});
    }, [user?.id, getPastJobPostingsAnalysis]);

    useEffect(() => {
        if (selectedResume) {
            setAvailableVersions(selectedResume.resumeVersions.filter(v => !v.isDraft));
            setSelectedVersion(null);
        }
    }, [selectedResume]);

    useEffect(() => {
        if (!selectedJobId) return;
        getPastJobAnalysisByJobPostingId(selectedJobId).then(dto => {
            if (dto?.jobDescription) setJdText(dto.jobDescription);
        }).catch(() => {});
    }, [selectedJobId, getPastJobAnalysisByJobPostingId]);

    const canRun = !!selectedVersion && jdText.trim().length > 50 && !loading;

    const handleRun = async () => {
        if (!canRun || !user?.id || !selectedVersion) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const response = await runRecruiterReview({
                job_description: jdText.trim(),
                resume: selectedVersion.resumeText,
                job_posting_id: selectedJobId || undefined,
                resume_version_id: selectedVersion.resumeVersionId,
            });
            setResult(response);
        } catch (e: any) {
            const data = e?.response?.data;
            setError(
                (typeof data === 'string' ? data : data?.detail ?? data?.title ?? data?.message)
                ?? e?.message
                ?? 'Something went wrong. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const scoreClass = (n: number) => (n >= 70 ? 'high' : n >= 45 ? 'mid' : 'low');
    return (
        <div className="rr-page">
            <div className="rr-header">
                <h1>Recruiter Review</h1>
                <p>Evaluate a candidate's fit through an evidence-first recruiter lens.</p>
            </div>

            {/* ── Inputs ── */}
            <div className="rr-form">
                <div className="rr-form-grid">
                    <div className="rr-form-group">
                        <label>Resume</label>
                        <select
                            className="rr-select"
                            value={selectedResume?.resumeId ?? ''}
                            onChange={e => {
                                const r = resumes.find(r => r.resumeId === e.target.value) ?? null;
                                setSelectedResume(r);
                            }}
                            disabled={resumesBusy}
                        >
                            <option value="">— Select a resume —</option>
                            {resumes.filter(r => !r.isArchived).map(r => (
                                <option key={r.resumeId} value={r.resumeId}>
                                    {r.resumeTitle}{r.isDefault ? ' (Default)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="rr-form-group">
                        <label>Resume Version</label>
                        <select
                            className="rr-select"
                            value={selectedVersion?.resumeVersionId ?? ''}
                            onChange={e => {
                                const v = availableVersions.find(v => v.resumeVersionId === e.target.value) ?? null;
                                setSelectedVersion(v);
                            }}
                            disabled={!selectedResume || availableVersions.length === 0}
                        >
                            <option value="">— Select a version —</option>
                            {availableVersions.map(v => (
                                <option key={v.resumeVersionId} value={v.resumeVersionId}>
                                    v{v.version}{v.changeSummary ? ` — ${v.changeSummary}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="rr-form-group">
                        <label>Saved Job Posting (optional)</label>
                        <select
                            className="rr-select"
                            value={selectedJobId}
                            onChange={e => setSelectedJobId(e.target.value)}
                        >
                            <option value="">— Select to pre-fill description —</option>
                            {jobPostings.map(j => (
                                <option key={j.jobPostingId} value={j.jobPostingId}>
                                    {j.title} — {j.company}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="rr-form-group">
                        <label>Job Description</label>
                        <textarea
                            className="rr-jd-textarea"
                            placeholder="Paste the job description here…"
                            value={jdText}
                            onChange={e => setJdText(e.target.value)}
                        />
                    </div>
                </div>

                <div className="rr-form-row">
                    {error ? <span className="rr-error">{error}</span> : <span />}
                    <button className="rr-btn-run" onClick={handleRun} disabled={!canRun}>
                        {loading ? 'Running Review…' : 'Run Recruiter Review'}
                    </button>
                </div>
            </div>

            {/* ── Results ── */}
            {result && (
                <div className="rr-results">

                    {/* Scores */}
                    <div className="rr-scores-strip">
                        <div className="rr-score-card">
                            <div className={`rr-score-dial ${scoreClass(result.candidate_match_score)}`}>
                                {result.candidate_match_score}
                            </div>
                            <div>
                                <div className="rr-score-label">Candidate Match Score</div>
                                <div className="rr-score-sub">Based on evidence in submission</div>
                            </div>
                        </div>
                        <div className="rr-score-card">
                            <div className={`rr-score-dial ${result.submission_confidence.toLowerCase()}`}>
                                {result.submission_confidence.charAt(0)}
                            </div>
                            <div>
                                <div className="rr-score-label">Submission Confidence</div>
                                <div className="rr-score-sub">{result.submission_confidence} Confidence</div>
                            </div>
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="rr-card">
                        <div className="rr-card-title">Overall Summary</div>
                        <p className="rr-summary-text">{result.overall_summary}</p>
                    </div>

                    {/* Strongest / Weakest Evidence */}
                    <div className="rr-two-col">
                        <div className="rr-card">
                            <div className="rr-card-title">Strongest Evidence</div>
                            <div className="rr-evidence-list">
                                {result.strongest_evidence.length === 0
                                    ? <span className="rr-none-msg">No strong evidence identified.</span>
                                    : result.strongest_evidence.map((e, i) => (
                                        <div key={i} className="rr-evidence-item strong">
                                            <div className="rr-evidence-label">{e}</div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                        <div className="rr-card">
                            <div className="rr-card-title">Weakest Evidence</div>
                            <div className="rr-evidence-list">
                                {result.weakest_evidence.length === 0
                                    ? <span className="rr-none-msg">No notable gaps identified.</span>
                                    : result.weakest_evidence.map((e, i) => (
                                        <div key={i} className="rr-evidence-item weak">
                                            <div className="rr-evidence-label">{e}</div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </div>

                    {/* Skill Evidence Table */}
                    <div className="rr-card">
                        <div className="rr-card-title">Skill Evidence</div>
                        <table className="rr-skill-table">
                            <thead>
                                <tr>
                                    <th>Skill</th>
                                    <th>Strength</th>
                                    <th>Evidence</th>
                                </tr>
                            </thead>
                            <tbody>
                                {result.skill_evidence.map((s, i) => (
                                    <tr key={i}>
                                        <td>
                                            {s.requirement_level === 'Required' && <span className="rr-required-dot" title="Required skill" />}
                                            {s.skill_name}
                                        </td>
                                        <td>
                                            <span className={`rr-skill-badge ${s.evidence_strength.toLowerCase()}`}>
                                                {s.evidence_strength}
                                            </span>
                                        </td>
                                        <td className="rr-evidence-cell">
                                            {s.resume_evidence ?? s.concern ?? <span style={{ color: '#475569' }}>—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Verification Questions + Listen For */}
                    <div className="rr-two-col">
                        <div className="rr-card">
                            <div className="rr-card-title">Verification Questions</div>
                            {result.verification_questions.length === 0
                                ? <span className="rr-none-msg">None generated.</span>
                                : (
                                    <ul className="rr-list">
                                        {result.verification_questions.map((q, i) => (
                                            <li key={i} data-prefix={`${i + 1}.`}>
                                                <strong>{q.skill_name}:</strong> {q.question}
                                                <div className="rr-listen-context">{q.why_ask}</div>
                                            </li>
                                        ))}
                                    </ul>
                                )
                            }
                        </div>
                        <div className="rr-card">
                            <div className="rr-card-title">Listen For</div>
                            {result.verification_questions.every(q => q.listen_for.length === 0)
                                ? <span className="rr-none-msg">No signals defined.</span>
                                : (
                                    <ul className="rr-list">
                                        {result.verification_questions.flatMap(q =>
                                            q.listen_for.map(signal => ({ skill: q.skill_name, signal }))
                                        ).map((s, i) => (
                                            <li key={i} data-prefix="◎">
                                                <strong>{s.skill}:</strong> {s.signal}
                                            </li>
                                        ))}
                                    </ul>
                                )
                            }
                        </div>
                    </div>

                    {/* Red Flags */}
                    <div className="rr-card">
                        <div className="rr-card-title">Red Flags</div>
                        {result.red_flags.length === 0
                            ? <span className="rr-none-msg">No red flags identified.</span>
                            : (
                                <div className="rr-flag-list">
                                    {result.red_flags.map((f, i) => (
                                        <div key={i} className="rr-flag-item">
                                            <span className="rr-flag-icon">
                                                <i className="fas fa-exclamation-triangle" />
                                            </span>
                                            {f}
                                        </div>
                                    ))}
                                </div>
                            )
                        }
                    </div>

                    <div className="rr-card">
                        <div className="rr-card-title">Recruiter Notes</div>
                        {result.recruiter_notes.length === 0
                            ? <span className="rr-none-msg">No additional notes.</span>
                            : (
                                <ul className="rr-list">
                                    {result.recruiter_notes.map((note, i) => (
                                        <li key={i} data-prefix="•">{note}</li>
                                    ))}
                                </ul>
                            )
                        }
                    </div>

                    {/* Recommended Next Step */}
                    <div className="rr-next-step">
                        <span className="rr-next-step-icon">→</span>
                        <div>
                            <div className="rr-next-step-label">Recommended Next Step</div>
                            <div className="rr-next-step-text">{result.recommended_next_step}</div>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

export default RecruiterReviewPage;
